import React, {useMemo, useState} from "react";
import { Table, Tooltip } from "antd";
import { EyeOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Actions from "./Actions";
import StatusBadge from "views/components/common/statusbadge/StatusBadge";
import ClaimAge, { ClaimAgeLegend } from "../components/ClaimAge";
import { computeAverageAgeMs } from "../services/claimAgeUtil";
import { isFailedStatus } from "../components/FailureDrawer";
import DocumentPreviewDrawer from "../components/DocumentPreviewDrawer";
import {
  getOpenedMap,
  getLastOpenedId,
  markDocumentOpened,
} from "../services/openedTracker";
import { saveQueueScrollTop } from "../services/queueScroll";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CommonAction from "stores/common/CommonAction";
import { useDispatch } from "react-redux";
import { getAnimalEmoji } from "constants/animalAvatars";

const PageTableview = ({ data, handler, selectedRowKeys = [], onSelectChange, onOpenFailure, onDataChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedHistory, setSelectedHistory] = useState(null);
  const pageOptions = useSelector((state) => state.common.options);

  const avgAgeMs = useMemo(
    () => computeAverageAgeMs((data || []).map((row) => row.received_at)),
    [data]
  );

  const handleSelectedHistory = (id) => {
    setSelectedHistory(id);
  }
  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const formatDate = (isoDate) => {
    if (!isoDate) return t("notAvailable");
    const date = new Date(isoDate);
    const weekday = date.toLocaleDateString(t("currentLanguage"), {
      weekday: "short",
      timeZone: "Europe/Paris",
    });
    const month = date.toLocaleDateString(t("currentLanguage"), {
      month: "short",
      timeZone: "Europe/Paris",
    });
    const day = date.toLocaleDateString(t("currentLanguage"), {
      day: "2-digit",
      timeZone: "Europe/Paris",
    });
    const time = date.toLocaleTimeString(t("currentLanguage"), {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    });
    return `${capitalize(weekday)} ${day} ${capitalize(month)} ${time}`;
  };

  /** Shorter stamp for the Last Modified column (no weekday). */
  const formatCompactDate = (isoDate) => {
    if (!isoDate) return t("notAvailable");
    const date = new Date(isoDate);
    const month = date.toLocaleDateString(t("currentLanguage"), {
      month: "short",
      timeZone: "Europe/Paris",
    });
    const day = date.toLocaleDateString(t("currentLanguage"), {
      day: "2-digit",
      timeZone: "Europe/Paris",
    });
    const time = date.toLocaleTimeString(t("currentLanguage"), {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    });
    return `${day} ${capitalize(month)} ${time}`;
  };

  const handleSort = (pagination, filters, sorter) => {
    customSortFunction(sorter);
  };

  const customSortFunction = (sorter) => {
    const { field, order } = sorter;

    let sortOrder;
    let sortField = field;

    if (order === "ascend") {
      sortOrder = "ASC";
    } else if (order === "descend") {
      sortOrder = "DESC";
    } else {
      sortField = "file_name";
      sortOrder = "ASC";
    }
    handler(sortField, sortOrder);
  };

  const openDocument = (record) => {
    const position = saveQueueScrollTop();
    markDocumentOpened(record.id);
    dispatch(CommonAction.setSelectedDocument(record.id));
    dispatch(CommonAction.setCurrentPosition(position));
    navigate(`/invoice/${record.id}`);
  };

  const [preview, setPreview] = useState({
    open: false,
    id: null,
    title: "",
    url: "",
  });
  const openedMap = getOpenedMap();
  const lastOpenedId = getLastOpenedId();

  // preview works for every document, including failed extractions that
  // cannot be opened for correction
  const openPreview = (record) => {
    setPreview({
      open: true,
      id: record.id,
      title: record.file_name,
      url: record.sas_document_link,
    });
  };

  const handleRowClick = (record) => {
    if (isFailedStatus(record.extraction_status)) {
      if (onOpenFailure) onOpenFailure(record);
    } else {
      openDocument(record);
    }
  };

  const translatePageCount = (count) => {
    if (count && count <= 1) {
      return `${count} ${t("page")}`;
    } else if (count && count > 1) {
      return `${count} ${t("pages")}`;
    } else {
      return "";
    }
  };

  const recipientNameHandle = (name) => {
    if (!name || name === "") {
      return t("notAvailable");
    }
    let nameParts = name.split(" ");

    if (nameParts.length === 1) {
      let singleNameInitials = nameParts[0].substring(0, 2).toUpperCase();
      return singleNameInitials;
    }

    let firstNameInitial = nameParts[0].charAt(0);
    let lastName = nameParts[1];

    let lastNameInitials =
      lastName.length >= 2 ? lastName.substring(0, 2) : lastName.charAt(0);

    return `${firstNameInitial}${lastNameInitials}`;
  };

  const columnsFromResponse = [
    {
      title: t("documentName"),
      dataIndex: "file_name",
      key: "file_name",
      width: 200,
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text} placement="topLeft">
          <StatusBadge type="extraction" status={record.extraction_status} />
          <span
            id={record.id}
            className="fs14 white-space-nowrap poppins-medium dark-text cursor-pointer ps-2"
            onClick={(e) => {
              e.stopPropagation();
              openPreview(record);
            }}
          >
            {text}
          </span>
          <Tooltip title={t("previewDocument")} placement="bottom">
            <button
              type="button"
              className="circle-btn ms-2"
              aria-label={t("previewDocument")}
              onClick={(e) => {
                e.stopPropagation();
                openPreview(record);
              }}
            >
              <EyeOutlined style={{ color: "#667085" }} />
            </button>
          </Tooltip>
          {openedMap[record.id] && (
            <Tooltip
              title={t("openedAt", {
                time: new Date(openedMap[record.id]).toLocaleString(
                  t("currentLanguage"),
                  {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                ),
              })}
              placement="bottom"
            >
              <ClockCircleOutlined
                className="ms-2"
                style={{ color: "#98A2B3", fontSize: 12 }}
              />
            </Tooltip>
          )}
        </Tooltip>
      ),
      sorter: true,
      sortOrder:
        pageOptions.sortBy === "file_name"
          ? pageOptions.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("operator"),
      dataIndex: "last_modified_by",
      key: "last_modified_by",
      width: 110,
      ellipsis: true,
      sorter: true,
      sortOrder:
        pageOptions.sortBy === "last_modified_by"
          ? pageOptions.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (text, record) => {
        const initials = recipientNameHandle(text);
        const animalEmoji = getAnimalEmoji(record?.animal_avatar);

        if (initials === "Invalid input") {
          return (
            <Tooltip title={text ? text : t("notAvailable")}>
              <span className="cell-gray fs14 white-space-nowrap cursor-pointer">
                {text}
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={text || t("notAvailable")}>
            <div
              className={`${
                text ? "round-icon" : "round-icon-empty font-400 fs14"
              }${animalEmoji ? " round-icon--animal" : ""}`}
              aria-label={text || t("notAvailable")}
            >
              {animalEmoji || initials}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t("receiverName"),
      dataIndex: "recipient",
      key: "recipient",
      width: 130,
      ellipsis: true,
      sorter: true,
      sortOrder:
        pageOptions.sortBy === "recipient"
          ? pageOptions.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (text) => (
        <Tooltip title={text}>
          <span className="cell-gray fs14 white-space-nowrap cursor-pointer">
            {text || t("notAvailable")}
          </span>
        </Tooltip>
      ),
    },
    {
      title: <ClaimAgeLegend />,
      dataIndex: "received_at",
      key: "received_at",
      width: 132,
      render: (text) => (
        <ClaimAge
          extractedAt={text}
          avgAgeMs={avgAgeMs}
          absoluteLabel={formatDate(text)}
          variant="table"
        />
      ),
      sorter: true,
      sortOrder:
        pageOptions.sortBy === "received_at"
          ? pageOptions.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("lastModified"),
      dataIndex: "last_modified_at",
      key: "last_modified_at",
      width: 118,
      render: (text) => (
        <Tooltip title={formatDate(text)} placement="topLeft">
          <span className="cell-gray fs12 white-space-nowrap last-modified-cell">
            {formatCompactDate(text)}
          </span>
        </Tooltip>
      ),
      sorter: true,
      sortOrder:
        pageOptions.sortBy === "last_modified_at"
          ? pageOptions.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("noOfPages"),
      dataIndex: "number_of_pages",
      key: "number_of_pages",
      width: 100,
      sorter: true,
      sortOrder:
        pageOptions.sortBy === "number_of_pages"
          ? pageOptions.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (text) => (
        <span className="cell-gray fs14 white-space-nowrap">
          {translatePageCount(text)}
        </span>
      ),
    },
    {
      title: t("status"),
      dataIndex: "updated_status",
      key: "updated_status",
      width: 150,
      render: (text, record) => (
        <StatusBadge type="updation" status={record.updated_status} />
      ),
    },
    {
      title: t("action"),
      key: "action",
      width: 120,
      align: 'left',
      render: (_, record) => (
        <span className="row-action-cell" onClick={(e) => e.stopPropagation()}>
          <Actions
            selectedHistory={selectedHistory}
            handleSelectedHistory={handleSelectedHistory}
            record={record}
            onOpenFailure={onOpenFailure}
            onDataChanged={onDataChanged}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="bg-white extractpdf-cardcontent table-view-content px-0">
        <div className="row p-0 mx-0">
          <Table
            columns={columnsFromResponse}
            dataSource={data}
            pagination={false}
            showSorterTooltip={false}
            scroll={{ x: 1200 }}
            sticky={true}
            onChange={handleSort}
            rowKey="id"
            className="queue-table"
            rowClassName={(record) =>
              [
                "queue-row",
                record.id === lastOpenedId ? "queue-row--last-opened" : "",
              ]
                .filter(Boolean)
                .join(" ")
            }
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => onSelectChange && onSelectChange(keys),
            }}
          />
        </div>
      </div>
      <DocumentPreviewDrawer
        open={preview.open}
        title={preview.title}
        url={preview.url}
        fileName={preview.title}
        documentId={preview.id}
        onClose={() => setPreview({ open: false, id: null, title: "", url: "" })}
      />
    </>
  );
};
export default PageTableview;
